import {
  CE_COMMA_DOT_DECIMAL_LOCALES,
  CE_LOCALES,
  DataObject,
  columnMeasurementMap,
  columnMinMaxMeasurementMap,
  columnValueMeasurementMap,
  formatMeasurementDescription,
} from '@miq/shared-models';
import { Column } from '@miq/catalog';
import { getCurrentLocale } from '@miq/utils';
import { DataCellFormatterPipe } from '../pipes/data-cell-formatter.pipe';

/**
 * The catalog VALUE FORMATTERS for the measurement columns, plus the building-facing-direction
 * formatter that sat next to them.
 *
 * L2-S4: moved out of `@records/records-search/config/measurements-override.config.ts` and
 * `columns-override.config.ts` (both now re-export from here) so `ExcelSharedService` could move
 * into this library — those two config modules value-import the records catalog/context graph,
 * which is exactly what the reports remote is being separated from. The formatters themselves are
 * pure functions of (recordData, column); their only real dependency is `DataCellFormatterPipe`,
 * which already lives here, and the column-id maps, which moved to @miq/shared-models.
 *
 * Moved verbatim — the CE locale number-formatting behaviour is unchanged.
 */

/**
 * Helper function to format numbers with proper locale support and CE dot-comma fix
 * Ensures 3000 becomes 3.000 for Austria/CE dot-comma locales
 */
const formatLocaleNumber = (value: number | string, locale: string): string => {
  const numValue = Number(value);
  if (isNaN(numValue)) return String(value);

  // Use Intl.NumberFormat with explicit options to ensure proper grouping
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 20,
    useGrouping: true,
  });
  let result = formatter.format(numValue);

  // Fix CE dot-comma locales: replace non-breaking space and regular space with dot
  if (locale && CE_COMMA_DOT_DECIMAL_LOCALES.includes(locale)) {
    // Replace all whitespace characters (space, non-breaking space \u00A0, etc.) with dot
    result = result.replace(/[\s\u00A0]/g, '.');
  }

  return result;
};
const propertyElementTypeColumn: Partial<Column> = {
  columnId: 3874,
  field: 'property_measurement/property_element_type_desc',
  dataType: 'text',
  displayAllValues: true,
  filterDuplicates: false,
};
/**
 * Common method to filter and sort measurements
 * @param measurements - Array of measurement objects
 * @returns Filtered and sorted measurements
 */
const getFiltereddMeasurements = (measurements: any[]) => {
  return measurements
    .filter(measurement => measurement?.property_element_type_desc !== 'Total Area')
    .sort((a, b) => {
      const oa: number | null = a?.display_order ?? null;
      const ob: number | null = b?.display_order ?? null;
      if (oa == null && ob == null) return 0;
      if (oa == null) return 1;
      if (ob == null) return -1;
      return oa - ob;
    });
};
const parseLovData = (lovDataString: any): any[] => {
  if (!lovDataString || typeof lovDataString !== 'string') {
    return [];
  }
  try {
    let content = lovDataString.trim();
    // Handle JSON array format first (existing functionality)
    if (content.startsWith('["') && content.endsWith('"]')) {
      content = content.slice(2, -2);
      return content.split(' | ').map(item => JSON.parse(item.trim()));
    }
    // Handle pipe-separated JSON objects (existing functionality)
    if (content.includes(' | ') && content.includes('{')) {
      return content.split(' | ').map(item => JSON.parse(item.trim()));
    }
    // Handle comma-separated simple values (new functionality)
    if (content.includes(',') && !content.includes('{') && !content.includes('[')) {
      return content.split(',').map(item => {
        const trimmedItem = item.trim();
        return {
          key: trimmedItem,
          value: trimmedItem,
          desc: trimmedItem,
        };
      });
    }
    // Handle pipe-separated simple values
    if (content.includes(' | ') && !content.includes('{')) {
      return content.split(' | ').map(item => {
        const trimmedItem = item.trim();
        return {
          key: trimmedItem,
          value: trimmedItem,
          desc: trimmedItem,
        };
      });
    }
    // Handle single value
    return [
      {
        key: content,
        value: content,
        desc: content,
      },
    ];
  } catch (error) {
    // Log specific error and return fallback
    console.error('Error parsing LOV data');
    console.debug('All parsing methods failed for LOV data:', {
      input: lovDataString,
      error: error.message,
    });
    return [];
  }
};
export const propertyElementTypeFormatter = (recordData: DataObject, column: Column, context?: any) => {
  // Map column IDs to their respective property paths
  if (!column?.columnId) {
    return '';
  }
  const propertyPath = columnMeasurementMap[column.columnId];
  const measurements = propertyPath ? recordData?.[propertyPath] : undefined;
  if (!measurements || !Array.isArray(measurements)) {
    return '';
  }
  // Use common filtering method
  const filteredMeasurements = getFiltereddMeasurements(measurements);
  // Format the descriptions from the filtered measurements
  const typeDescriptions = filteredMeasurements.map(measurement => formatMeasurementDescription(measurement));
  if (typeDescriptions.length === 0) {
    return '';
  }
  try {
    // Get lookup data efficiently
    const lovDataString = DataCellFormatterPipe.extractValue(recordData, column, (lovValue: DataObject) =>
      JSON.stringify(lovValue)
    );
    // Parse lookup data
    let lookupArray: any[] = [];
    if (typeof lovDataString === 'string' && lovDataString.trim()) {
      lookupArray = parseLovData(lovDataString);
    } else if (Array.isArray(lovDataString)) {
      lookupArray = lovDataString;
    }
    // Early return if no lookup data
    if (!Array.isArray(lookupArray) || lookupArray.length === 0) {
      return typeDescriptions.join(' | ');
    }
    // Create lookup map for O(1) performance instead of O(n) find()
    const lookupMap = new Map(lookupArray.map(item => [item?.key, item?.value]));
    // Transform using map lookup while maintaining sorted order
    const transformedDescriptions = typeDescriptions.map(desc => {
      // Try exact match first
      let mapped = lookupMap.get(desc);
      if (mapped) return mapped;
      // Try prefix match if not found
      for (const [key, value] of lookupMap.entries()) {
        if (key && desc.startsWith(key)) {
          // Append the suffix (e.g., the number) to the mapped value
          const suffix = desc.slice(key.length).trim();
          return suffix ? `${value} ${suffix}` : value;
        }
      }
      return desc;
    });
    return transformedDescriptions.join(' | ');
  } catch (error) {
    // Log the error for debugging purposes
    console.warn('Error parsing lookup data in propertyElementTypeFormatter');
    // Log the error object using a debug logging level
    console.debug('Error details:', error);
    // Return the raw type descriptions as fallback
    return typeDescriptions.join(' | ');
  }
};
export const propertyMeasurementValueFormatter = (recordData: DataObject, column?: Column) => {
  // Extract all property_measurement_size from measurements using optional chaining
  if (!column?.columnId) {
    return '';
  }
  const propertyValuePath = columnValueMeasurementMap[column.columnId];
  const measurements = propertyValuePath ? recordData?.[propertyValuePath] : undefined;
  if (!measurements || !Array.isArray(measurements)) {
    return '';
  }
  // Use common filtering method
  const filteredMeasurements = getFiltereddMeasurements(measurements);
  // Format measurement values from filtered measurements
  const measurementValues = filteredMeasurements.map(measurement => {
    const size = measurement?.property_measurement_size;
    // Handle null/undefined/empty values - show hyphen instead of filtering out
    if (size == null || size === '' || isNaN(Number(size))) {
      return '-';
    }
    const currentLocale = getCurrentLocale();
    const locale = CE_LOCALES.includes(currentLocale) ? currentLocale : 'en-US';
    // Format with locale-aware number formatting using helper function
    return formatLocaleNumber(size, locale);
  });
  return measurementValues.length > 0 ? measurementValues.join(' | ') : '';
};
export const propertyMeasurementSizeMaxFormatter = (recordData: DataObject, column?: Column) => {
  if (!column?.columnId) {
    return '';
  }
  const propertyValuePath = columnMinMaxMeasurementMap[column.columnId];
  const measurements = propertyValuePath ? recordData?.[propertyValuePath] : undefined;
  if (!measurements || !Array.isArray(measurements)) {
    return '';
  }
  const filteredMeasurements = getFiltereddMeasurements(measurements);
  const currentLocale = getCurrentLocale();
  const locale = CE_LOCALES.includes(currentLocale) ? currentLocale : 'en-US';
  const measurementValues = filteredMeasurements.map(measurement => {
    const min = measurement?.property_measurement_size_min;
    const max = measurement?.property_measurement_size_max;
    const uom = measurement?.property_measurement_size_max_uom;
    const hasMin = min != null && min !== '' && !isNaN(Number(min));
    const hasMax = max != null && max !== '' && !isNaN(Number(max));
    if (hasMin) {
      const minFormatted = formatLocaleNumber(min, locale);
      const maxFormatted = hasMax ? formatLocaleNumber(max, locale) : '-';
      return uom ? `${minFormatted} - ${maxFormatted} ${uom}` : `${minFormatted} - ${maxFormatted}`;
    }
    if (hasMax) {
      const maxFormatted = formatLocaleNumber(max, locale);
      return uom ? `${maxFormatted} ${uom}` : maxFormatted;
    }
    return '-';
  });
  return measurementValues.length > 0 ? measurementValues.join(' | ') : '';
};
export const propertyMeasurementElementNumberFormatter = (recordData: DataObject, column?: Column) => {
  if (!column?.columnId) {
    return '';
  }
  const propertyValuePath = columnValueMeasurementMap[column.columnId];
  const measurements = propertyValuePath ? recordData?.[propertyValuePath] : undefined;
  if (!measurements || !Array.isArray(measurements)) {
    return '';
  }
  // Use common filtering method
  const filteredMeasurements = getFiltereddMeasurements(measurements);
  const currentLocale = getCurrentLocale();
  const locale = CE_LOCALES.includes(currentLocale) ? currentLocale : 'en-US';
  // Format measurement values from filtered measurements
  const measurementValues = filteredMeasurements.map(measurement => {
    const unitNo = measurement?.element_number;
    // Handle null/undefined/empty values - show hyphen instead of filtering out
    if (unitNo == null || unitNo === '' || isNaN(Number(unitNo))) {
      return '-';
    }
    // Format with locale-aware number formatting using helper function
    return formatLocaleNumber(unitNo, locale);
  });
  return measurementValues.length > 0 ? measurementValues.join(' | ') : '';
};

export const buildingFacingDirectionFormatter = (recordData: DataObject, column?: Column): string => {
  if (!column?.columnId || !Array.isArray(recordData?.usages)) {
    return '';
  }
  const usageDirectionMap = new Map<string, string>();
  recordData.usages.forEach(usage => {
    const usageType = usage?.ref_property_usage_type_desc;
    const direction = usage?.building_facing_direction_type_desc;
    if (usageType) {
      usageDirectionMap.set(usageType, direction || '-');
    }
  });
  return Array.from(usageDirectionMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, direction]) => direction)
    .join(' | ');
};
